<script setup lang="ts">
import DashboardMixingCycleCard from '@/components/Dashboard/Body/MixingCycle/DashboardMixingCycleCard.vue'
import ChartMarker from '@/components/Globals/Charts/ChartMarker.vue'
import type { ILineChartMarker } from '@/interfaces/components/chats/chart-marker'
import type { IGroupTotalizer, IDashboardSearch, ISensorTotalizer } from '@/interfaces/stores/dashboard'
import { useCompanyStore } from '@/store/company'
import { useDashboardStore } from '@/store/dashboard'
import { formatDashboardChartParams } from '@/utils/components/dashboard-totalizers'
import { formatDashboardMinutesFromApi } from '@/utils/date/date-time'
import { formatDashboardPeriodRangeLabel, normalizeDashboardPeriod } from '@/utils/dashboard-workspace'
import { isMobileWindow } from '@/utils/screen/window'
import { BoltIcon, ClockIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import VueApexCharts from 'vue3-apexcharts'

const props = defineProps<{
  search: IDashboardSearch
  showInlineChart: boolean
  compactLayout: boolean
}>()

const { t } = useI18n()
const dashboardStore = useDashboardStore()
const { hasMixingCyclesEnabled } = storeToRefs(useCompanyStore())

const periodDates = computed(() => normalizeDashboardPeriod(props.search.period))

const periodLabel = computed(() => formatDashboardPeriodRangeLabel(props.search.period))

const isSensorBlock = computed(() => props.search.type === 'sensor')

const sensorTotalizer = computed(() => props.search.totalizer as ISensorTotalizer)
const groupTotalizer = computed(() => props.search.totalizer as IGroupTotalizer)

function formatDashboardLocaleNumber(
  value: number | undefined | null,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
  suffix: string
): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits, maximumFractionDigits })}${suffix}`
}

function formatKwh(value: number | undefined | null) {
  return formatDashboardLocaleNumber(value, 1, 1, ' kWh')
}

function formatKw(value: number | undefined | null) {
  return formatDashboardLocaleNumber(value, 1, 1, ' kW')
}

function formatPct(value: number | undefined | null) {
  return formatDashboardLocaleNumber(value, 0, 0, '%')
}

const cardTotalConsumption = computed(() => {
  if (isSensorBlock.value) return formatKwh(sensorTotalizer.value.consumption?.total_consumption_kwh)
  return formatKwh(groupTotalizer.value.total_consumption_kwh)
})

type PeakOffVariant = 'peak' | 'offPeak'

function buildPeakOffDemandCard(
  isSensor: boolean,
  sensor: ISensorTotalizer,
  group: IGroupTotalizer,
  variant: PeakOffVariant
): { kwh: string; kw: string } {
  if (isSensor) {
    const { consumption, demand } = sensor
    if (variant === 'peak') {
      return {
        kwh: formatKwh(consumption?.peak_consumption_kwh),
        kw: formatKw(demand?.peak_demand_kw),
      }
    }
    return {
      kwh: formatKwh(consumption?.off_peak_consumption_kwh),
      kw: formatKw(demand?.off_peak_demand_kw),
    }
  }
  return {
    kwh: formatKwh(variant === 'peak' ? group.peak_consumption_kwh : group.off_peak_consumption_kwh),
    kw: formatKw(variant === 'peak' ? group.peak_demand_max : group.off_peak_demand_max),
  }
}

const cardPeak = computed(() =>
  buildPeakOffDemandCard(isSensorBlock.value, sensorTotalizer.value, groupTotalizer.value, 'peak')
)

const cardOffPeak = computed(() =>
  buildPeakOffDemandCard(isSensorBlock.value, sensorTotalizer.value, groupTotalizer.value, 'offPeak')
)

const cardUtil = computed(() => {
  if (isSensorBlock.value) {
    const u = sensorTotalizer.value.utilization
    return {
      pct: formatPct(u?.utilization),
      used: formatDashboardMinutesFromApi(Number(u?.total_utilization_time ?? 0)),
      unused: formatDashboardMinutesFromApi(Number(u?.total_non_utilization_time ?? 0)),
    }
  }
  const g = groupTotalizer.value
  return {
    pct: formatPct(g.utilization_percentage),
    used: formatDashboardMinutesFromApi(Number(g.total_utilization_time ?? 0)),
    unused: formatDashboardMinutesFromApi(Number(g.total_non_utilization_time ?? 0)),
  }
})

const series = ref<Array<{ name?: string; data: { x: Date; y: number }[] }>>([])
const chartOptions = ref<Record<string, unknown>>({})
const chartLoaded = ref(false)
const fixedMarkers = ref<ILineChartMarker[]>([])

function mergeCompactInlineChartOptions(opts: Record<string, unknown>): Record<string, unknown> {
  if (!props.compactLayout) return opts
  const legend = opts.legend as Record<string, unknown> | undefined
  const stroke = opts.stroke as Record<string, unknown> | undefined
  const xa = (opts.xaxis ?? {}) as Record<string, unknown>
  const xLabels = (xa.labels ?? {}) as Record<string, unknown>
  return {
    ...opts,
    legend: { ...legend, fontSize: '11px', offsetY: 0 },
    stroke: { ...stroke, width: 2 },
    xaxis: {
      ...xa,
      labels: {
        ...xLabels,
        style: { ...(xLabels.style as Record<string, unknown> | undefined), fontSize: '11px', fontWeight: 500 },
      },
    },
  }
}

function buildInlineChart() {
  if (!props.showInlineChart) {
    chartLoaded.value = false
    series.value = []
    return
  }
  const s = props.search
  const isSensor = isSensorBlock.value
  const readings = isSensor ? sensorTotalizer.value.readings : groupTotalizer.value.energy_sensors_readings
  if (!readings?.length) {
    chartLoaded.value = false
    return
  }
  const optionsConfig = {
    period: periodDates.value,
    chartIdSuffix: s.id,
    xaxisLabelsRotate: props.compactLayout ? -45 : 0,
    ...(isSensor ? { strokeColors: [s.color] } : {}),
  }
  const { seriesParams, chartOptionsParams } = formatDashboardChartParams(readings, s.label, isSensor, optionsConfig)
  series.value = seriesParams as typeof series.value
  chartOptions.value = mergeCompactInlineChartOptions(chartOptionsParams as Record<string, unknown>)
  chartLoaded.value = true
}

watch(
  () => [props.search, props.showInlineChart, props.compactLayout] as const,
  () => {
    buildInlineChart()
  },
  { deep: true, immediate: true },
)

function closeBlock() {
  dashboardStore.removeSearch(props.search.id)
}

const handleChartClick = (
  event: { target: { getBoundingClientRect: () => DOMRect }; clientX: number; clientY: number },
  _chartContext: unknown,
  config: { dataPointIndex: number; seriesIndex: number },
) => {
  if (isMobileWindow()) return
  if (config.dataPointIndex < 0 || config.seriesIndex < 0) return
  const ser = series.value[config.seriesIndex]
  if (!ser?.data?.[config.dataPointIndex]) return
  const dataPoint = ser.data[config.dataPointIndex]
  const chartRect = event.target.getBoundingClientRect()
  fixedMarkers.value.push({
    seriesName: String(ser.name ?? ''),
    x: dataPoint.x,
    y: `${dataPoint.y} kW`,
    positionX: event.clientX - chartRect.left,
    positionY: event.clientY - chartRect.top + 50,
  })
}

function onChartClicked(event: unknown, chartContext: unknown, config: { dataPointIndex: number; seriesIndex: number }) {
  handleChartClick(
    event as { target: { getBoundingClientRect: () => DOMRect }; clientX: number; clientY: number },
    chartContext,
    config,
  )
}

function clearMarkers() {
  fixedMarkers.value = []
}

const formatMixingCycleRows = computed(() => {
  const d = props.search.mixingCycleData
  if (!d) return []
  return [
    { value: d.cycle_count, label: 'cycle_count' as const },
    { value: formatDashboardMinutesFromApi(d.average_time), label: 'average_time' as const },
    { value: formatDashboardMinutesFromApi(d.total_time), label: 'total_time' as const },
  ]
})

/** Modo lista: métricas em linha; lado a lado (`compactLayout`): colunas estreitas, conteúdo vertical. */
const isListCardMetricsLayout = computed(() => !props.compactLayout)

/** Modo lista: altura padrão. Lado a lado com gráfico inline: mais alto para leitura; sem gráfico o valor não é usado. */
const inlineChartHeightPx = computed(() => {
  if (!props.compactLayout) return 260
  return props.showInlineChart ? 320 : 220
})

/** Lista: colunas mais estreitas nos 3 primeiros cartões e mais largura no de utilização (rótulos longos). */
const metricsCardsGridClass = computed(() =>
  props.compactLayout ? 'md:grid-cols-2' : 'lg:grid-cols-[minmax(0,0.74fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.46fr)]',
)

const metricCardInnerPadX = computed(() => (props.compactLayout ? 'px-4' : 'px-3'))

const metricCardTitleClass = computed(() => 'min-w-0 flex-1 truncate text-left text-sm font-bold uppercase')
</script>

<template>
  <article
    :id="`dashboard-block-${search.id}`"
    class="flex min-h-0 w-full min-w-0 max-w-full flex-col border border-stroke bg-white shadow-md"
    :style="{ borderTopWidth: '3px', borderTopColor: search.color }">
    <header class="flex flex-wrap items-start justify-between gap-2 border-b border-stroke px-4 py-3 sm:px-5 sm:py-4">
      <div class="flex min-w-0 flex-1 items-start gap-2">
        <span class="mt-1 h-3.5 w-3.5 shrink-0" :style="{ backgroundColor: search.color }" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h2
              class="min-w-0 font-satoshi text-lg font-bold leading-snug text-black sm:text-xl"
              :class="compactLayout ? 'break-words' : 'truncate'">
              {{ search.label }}
            </h2>
            <span
              v-if="!isSensorBlock"
              class="shrink-0 self-center border border-stroke bg-gray-2 px-2 py-0.5 text-xs font-bold uppercase leading-none tracking-wide text-gray-700"
              role="status">
              {{ t('dashboard.block.group_badge') }}
            </span>
          </div>
          <p
            class="mt-1 text-sm font-medium text-gray-500"
            :class="compactLayout ? 'break-words' : 'truncate'">
            {{ periodLabel }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="flex h-9 w-9 shrink-0 items-center justify-center text-gray-500 hover:text-red"
        :aria-label="t('dashboard.block.close_search')"
        @click="closeBlock">
        <XMarkIcon class="h-5 w-5" aria-hidden="true" />
      </button>
    </header>

    <div
      class="grid min-w-0 grid-cols-1 items-stretch gap-3 p-4 [&>*]:min-w-0"
      :class="metricsCardsGridClass">
      <!-- Consumo total: cabeçalho + linha + corpo (altura igual aos demais no grid) -->
      <div class="flex h-full min-w-0 flex-col border border-stroke bg-gray-2">
        <div class="flex items-start gap-1.5 border-b border-stroke pb-2 pt-3" :class="metricCardInnerPadX">
          <BoltIcon class="h-6 w-6 shrink-0 text-gray-500" aria-hidden="true" />
          <span class="text-gray-700" :class="metricCardTitleClass">{{ t('dashboard.block.cards.total_consumption') }}</span>
        </div>
        <div
          class="flex min-h-0 min-w-0 flex-1 items-start pb-3 pt-3"
          :class="[metricCardInnerPadX, compactLayout ? 'flex-col' : 'flex-row gap-3']">
          <div class="min-w-0 text-left" :class="compactLayout ? 'w-full' : ''">
            <p
              class="truncate font-bold text-black"
              :class="compactLayout ? 'text-xl sm:text-2xl' : 'text-3xl sm:text-title-md'">
              {{ cardTotalConsumption }}
            </p>
            <p class="mt-1 truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.total_consumption') }}</p>
          </div>
        </div>
      </div>

      <div class="flex h-full min-w-0 flex-col border border-stroke bg-gray-2">
        <div class="flex items-start gap-1.5 border-b border-stroke pb-2 pt-3" :class="metricCardInnerPadX">
          <BoltIcon class="h-6 w-6 shrink-0 text-red" aria-hidden="true" />
          <span class="text-red" :class="metricCardTitleClass">{{ t('dashboard.block.cards.peak_title') }}</span>
        </div>
        <div
          class="min-h-0 min-w-0 flex-1 pb-3 pt-3 text-black"
          :class="[
            metricCardInnerPadX,
            isListCardMetricsLayout
              ? 'grid grid-cols-2 items-start gap-x-2 gap-y-1 text-lg font-semibold'
              : 'flex flex-col gap-3 text-lg font-bold',
          ]"
        >
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate leading-tight">{{ cardPeak.kwh }}</span>
            <span class="truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.peak_consumption') }}</span>
          </div>
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate leading-tight">{{ cardPeak.kw }}</span>
            <span class="truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.peak_demand') }}</span>
          </div>
        </div>
      </div>

      <div class="flex h-full min-w-0 flex-col border border-stroke bg-gray-2">
        <div class="flex items-start gap-1.5 border-b border-stroke pb-2 pt-3" :class="metricCardInnerPadX">
          <BoltIcon class="h-6 w-6 shrink-0 text-darkGreen" aria-hidden="true" />
          <span class="text-darkGreen" :class="metricCardTitleClass">{{ t('dashboard.block.cards.off_peak_title') }}</span>
        </div>
        <div
          class="min-h-0 min-w-0 flex-1 pb-3 pt-3 text-black"
          :class="[
            metricCardInnerPadX,
            isListCardMetricsLayout
              ? 'grid grid-cols-2 items-start gap-x-2 gap-y-1 text-lg font-semibold'
              : 'flex flex-col gap-3 text-lg font-bold',
          ]"
        >
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate leading-tight">{{ cardOffPeak.kwh }}</span>
            <span class="truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.off_peak_consumption') }}</span>
          </div>
          <div class="flex min-w-0 flex-col gap-1">
            <span class="truncate leading-tight">{{ cardOffPeak.kw }}</span>
            <span class="truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.off_peak_demand') }}</span>
          </div>
        </div>
      </div>

      <div class="flex h-full min-w-0 flex-col border border-stroke bg-gray-2">
        <div class="flex items-center justify-between gap-2 border-b border-stroke pb-2 pt-3" :class="metricCardInnerPadX">
          <div class="flex min-w-0 flex-1 items-center gap-1.5">
            <span class="h-3.5 w-3.5 shrink-0 self-center bg-blue" aria-hidden="true" />
            <span class="min-w-0 truncate text-sm font-bold uppercase leading-snug text-blue">
              {{ t('dashboard.block.cards.util_title') }}
            </span>
          </div>
          <span class="shrink-0 pt-px font-satoshi text-sm font-medium tabular-nums leading-none text-blue">
            {{ cardUtil.pct }}
          </span>
        </div>
        <div
          v-if="isListCardMetricsLayout"
          class="flex min-h-0 min-w-0 flex-1 flex-row flex-nowrap items-start justify-start gap-x-8 pb-3 pt-3 text-lg font-semibold text-black"
          :class="metricCardInnerPadX">
          <div class="flex shrink-0 items-start gap-1.5">
            <ClockIcon class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
            <div class="min-w-0">
              <p class="truncate">{{ cardUtil.used }}</p>
              <p class="text-xs font-medium leading-snug text-gray-500">{{ t('dashboard.block.cards.time_used') }}</p>
            </div>
          </div>
          <div class="flex shrink-0 items-start gap-1.5">
            <ClockIcon class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
            <div class="min-w-0">
              <p class="truncate">{{ cardUtil.unused }}</p>
              <p class="text-xs font-medium leading-snug text-gray-500">{{ t('dashboard.block.cards.time_unused') }}</p>
            </div>
          </div>
        </div>
        <div v-else class="flex min-h-0 min-w-0 flex-1 flex-col gap-3 pb-3 pt-3 text-lg font-bold text-black" :class="metricCardInnerPadX">
          <div class="flex min-w-0 flex-col gap-1.5">
            <p class="truncate leading-tight">{{ cardUtil.used }}</p>
            <div class="flex min-w-0 items-center gap-2">
              <ClockIcon class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
              <span class="min-w-0 truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.time_used') }}</span>
            </div>
          </div>
          <div class="flex min-w-0 flex-col gap-1.5">
            <p class="truncate leading-tight">{{ cardUtil.unused }}</p>
            <div class="flex min-w-0 items-center gap-2">
              <ClockIcon class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
              <span class="min-w-0 truncate text-xs font-medium text-gray-500">{{ t('dashboard.block.cards.time_unused') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showInlineChart && chartLoaded && series.length"
      class="min-w-0 max-w-full border-t border-stroke"
      :class="compactLayout ? 'px-1.5 pb-2 pt-4' : 'px-2 pb-3 pt-2'">
      <h3
        class="font-satoshi font-bold text-black"
        :class="compactLayout ? 'px-1.5 pb-1 text-sm' : 'px-2 pb-2 text-base'">
        {{ t('dashboard.body.chart.title') }}
      </h3>
      <div class="relative min-w-0 max-w-full overflow-hidden bg-white" :style="{ minHeight: `${inlineChartHeightPx}px` }">
        <VueApexCharts
          :key="`inline-chart-${search.id}-${compactLayout}`"
          :height="inlineChartHeightPx"
          width="100%"
          class="min-w-0 max-w-full"
          :options="chartOptions"
          :series="series"
          @click="onChartClicked"
          @updated="clearMarkers"
          @zoomed="clearMarkers"
          @panned="clearMarkers" />
        <ChartMarker v-for="(marker, index) in fixedMarkers" :key="index" :marker="marker" @remove="fixedMarkers.splice(index, 1)" />
      </div>
    </div>

    <div v-if="isSensorBlock && hasMixingCyclesEnabled" class="border-t border-stroke px-4 pb-4 pt-4">
      <h3 class="mb-3 font-satoshi text-base font-bold text-black">{{ t('dashboard.body.mixing_cycle.title') }}</h3>
      <div
        v-if="search.mixingCycleData"
        class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <DashboardMixingCycleCard v-for="(item, index) in formatMixingCycleRows" :key="index" :value="item.value" :label="item.label" />
      </div>
      <div v-else-if="search.mixingCycleLoading" class="py-4 text-center text-base font-medium text-gray-500">
        {{ t('dashboard.body.mixing_cycle.loading') }}
      </div>
      <div v-else class="py-4 text-center text-base font-medium text-gray-500">
        {{ t('dashboard.body.mixing_cycle.cards.no_data') }}
      </div>
    </div>
  </article>
</template>
